import { useState, useEffect, useRef } from 'react';
import { MdAddBox, MdClose, MdOutlineFavorite } from 'react-icons/md';
import { FaHeart } from 'react-icons/fa';
import { Modal, ModalBody, ModalContent, ModalOverlay } from '@chakra-ui/modal';

import Card from 'components/card';
import SearchIcon from 'components/icons/SearchIcon';
import { getProducts, getProductsAlreadyOrdered } from 'interfaces/products';
import { getCustomerLikedProducts } from 'interfaces/customer';
import { getLightStock } from 'interfaces/stock';

const AddProductsModal = (props: {
    isOpen: any;
    onClose: any;
    addProducts: any;
    existingProducts: any;
    customerId?: string;
}) => {
    const { isOpen, onClose, addProducts, existingProducts, customerId } = props;

    const [search, setSearch] = useState('');
    const [refSearch, setRefSearch] = useState('');
    const [eanSearch, setEanSearch] = useState('');
    const [tab, setTab] = useState('all');
    const [customProducts, setCustomProducts] = useState<any[]>([]);
    const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
    const [products, setProducts] = useState<any[]>([]);
    const [alreadyOrderedProducts, setAlreadyOrderedProducts] = useState<any[]>([]);
    const [likedProductIds, setLikedProductIds] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [allProductsLoaded, setAllProductsLoaded] = useState(false);
    const [useLightStock, setUseLightStock] = useState(true);
    const [selectedPackagings, setSelectedPackagings] = useState<{[key: string]: any}>({});
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const loadProducts = async (page: number, orderedProducts?: any[]) => {
        if (isLoading) return;
        setIsLoading(true);
        
        // For first load, try to use getLightStock to get ALL products at once
        if (useLightStock && page === 1) {
            const stockRes = await getLightStock();
            if (stockRes && stockRes.data) {
                // Filter only products (not ingredients)
                const onlyProducts = stockRes.data.filter((item: any) => item.type === 'product');
                
                const recentList = orderedProducts || alreadyOrderedProducts || [];
                const lastOrdersIds = recentList.map((order: any) => order._id);
                
                // Build a map from getLightStock data (which has draftQuantity)
                const stockMap = new Map(onlyProducts.map((p: any) => [String(p._id), p]));
                
                // Mark products as isRecent, preferring getLightStock data (which has draftQuantity)
                const allIds = new Set(lastOrdersIds.map(String));
                const formattedProducts = onlyProducts.map((product: any) => ({
                    ...product,
                    isRecent: allIds.has(String(product._id)) && product.name
                }));
                
                // For recent products, use getLightStock data enriched with isRecent flag
                // so that draftQuantity is preserved
                const recentProducts = recentList.map((p: any) => {
                    const stockData = stockMap.get(String(p._id));
                    return {
                        ...(stockData || p),
                        isRecent: true
                    };
                });
                
                // Filter out duplicates
                const nonRecentProducts = formattedProducts.filter((p: any) => !allIds.has(String(p._id)));
                
                setProducts([...recentProducts, ...nonRecentProducts]);
                setHasMore(false);
                setAllProductsLoaded(true);
                setIsLoading(false);
                
                // If we need customer-specific pricing, enrich
                if (customerId) {
                    enrichProductsWithCustomerPricing([...recentProducts, ...nonRecentProducts]);
                }
                return;
            } else {
                // Fallback to paginated API if light stock fails
                setUseLightStock(false);
            }
        }
        
        // Fallback: use paginated API
        const res = await getProducts(page, 100, customerId);
        if (res && res.data) {
            const recentList = orderedProducts || alreadyOrderedProducts || [];
            const lastOrdersIds = recentList.map((order: any) => order._id);
            
            // Mark products as isRecent without duplicating
            const formattedProducts = res.data.map((product: any) => ({
                ...product,
                isRecent: lastOrdersIds.includes(product._id) && product.name
            }));
            
            if (page === 1) {
                // On the first page, add already ordered products first
                const recentProducts = recentList.map((p: any) => ({
                    ...p,
                    isRecent: true
                }));
                
                // Filter out duplicates from the fetched products
                const allIds = new Set(lastOrdersIds);
                const nonRecentProducts = formattedProducts.filter((p: any) => !allIds.has(p._id));
                
                setProducts([...recentProducts, ...nonRecentProducts]);
            } else {
                setProducts((prev) => [...prev, ...formattedProducts]);
            }
            setHasMore(res.data.length === 100);
        }
        setIsLoading(false);
    };

    const enrichProductsWithCustomerPricing = async (productsToEnrich: any[]) => {
        if (!customerId) return;
        
        // Call the products API with customerId to get enriched data
        const enriched = await getProducts(1, productsToEnrich.length, customerId);
        if (enriched && enriched.data) {
            // Merge the pricing data from enriched into our products
            const enrichedMap = new Map(enriched.data.map((p: any) => [p._id, p]));
            const mergedProducts = productsToEnrich.map((p: any) => {
                const enrichedData: any = enrichedMap.get(p._id);
                if (enrichedData) {
                    return {
                        ...p,
                        price: enrichedData.price,
                        vat: enrichedData.vat ?? p.vat,
                        customPrice: enrichedData.customPrice,
                        alreadyOrdered: enrichedData.alreadyOrdered,
                        packagings: enrichedData.packagings || p.packagings,
                    };
                }
                return p;
            });
            setProducts(mergedProducts);
        }
    };

    const loadAlreadyOrderedProducts = async () => {
        if (!customerId) return [];
        const res = await getProductsAlreadyOrdered(customerId);
        if (res) {
            setAlreadyOrderedProducts(res);
            return res;
        }
        return [];
    };

    const formatProducts = (productsList: any[]) => {
        const lastOrders = alreadyOrderedProducts || [];
        const lastOrdersIds = lastOrders.map((order: any) => order._id);
        
        // Juste marquer les produits comme isRecent, sans les dupliquer
        return productsList.map((product: any) => ({
            ...product,
            isRecent: lastOrdersIds.includes(product._id) && product.name
        }));
    };

    // Merge freshly fetched products into the current list, deduplicated by _id.
    // Server results take precedence (up-to-date pricing) while preserving the
    // "recent first" ordering and any local-only flags already present.
    const mergeProducts = (incoming: any[]) => {
        if (!incoming || incoming.length === 0) return;
        const formatted = formatProducts(incoming);
        setProducts((prev) => {
            const byId = new Map(prev.map((p: any) => [String(p._id), p]));
            for (const p of formatted) {
                const id = String(p._id);
                byId.set(id, { ...(byId.get(id) || {}), ...p });
            }
            const merged = Array.from(byId.values());
            // Keep already-ordered products first, like the initial load does
            const recent = merged.filter((p: any) => p.isRecent);
            const others = merged.filter((p: any) => !p.isRecent);
            return [...recent, ...others];
        });
    };

    const handleItemClicked = (product: any, quantity: number) => {
        const selectedPackaging = selectedPackagings[product._id];
        
        const newProduct = {
            ...product,
            productId: product._id,
            price: selectedPackaging ? parseFloat(selectedPackaging.price) : parseFloat(product.price),
            unity: product.unity, // ALWAYS keep base unity, never change it
            vat: parseFloat(product.vat),
            quantity,
            discountType: 'none',
            discountValue: 0,
            // Add packaging info if selected
            ...(selectedPackaging && {
                packagingRef: selectedPackaging.ref,
                packagingUnity: selectedPackaging.unity,
                packagingWeight: selectedPackaging.weight,
                packagingBillByBaseUnit: selectedPackaging.billByBaseUnit || false,
            }),
        };
        if (!selectedProducts.find((i) => i.productId === newProduct.productId))
            setSelectedProducts((prev) => [...prev, newProduct]);
        else if (quantity <= 0)
            setSelectedProducts((prev) =>
                prev.filter((i) => i.productId !== newProduct.productId),
            );
        else
            setSelectedProducts((prev) =>
                prev.map((i) =>
                    i.productId === newProduct.productId
                        ? { ...i, quantity, price: newProduct.price, packagingRef: newProduct.packagingRef, packagingUnity: newProduct.packagingUnity, packagingWeight: newProduct.packagingWeight, packagingBillByBaseUnit: newProduct.packagingBillByBaseUnit }
                        : i,
                ),
            );
    };

    const searchProduct = (product: any, search: string, refSearch: string, eanSearch: string) => {

        const normalize = (value: any) =>
            String(value ?? '')
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '');

        const name = normalize(product.name);
        const ref = normalize(product.ref);
        const ean = normalize(product.ean || product.eanCode);
        const searchTerm = normalize(search).trim();
        const refTerm = normalize(refSearch).trim();
        const eanTerm = normalize(eanSearch).trim();

        if (!search && !refSearch && !eanSearch) return true;

        if (eanTerm) {
            if (!ean.includes(eanTerm)) return false;
            // if EAN matches and no other search, accept
            if (!searchTerm && !refTerm) return true;
        }

        if (refTerm) {
            if (!ref.includes(refTerm)) return false;
            // if ref matches and no name search, accept
            if (!searchTerm) return true;
        }

        // if name search only (or both)
        // split into terms and match fuzzy
        const terms = searchTerm.split(/\s+/).filter(Boolean);
        if (terms.length === 0) return true; // nothing meaningful to match

        const escapeRegex = (text: string) =>
            text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

        const fuzzify = (term: string): RegExp => {
            const escaped = escapeRegex(term);
            const length = term.length;

        if (length <= 0) {
            return new RegExp(`\\b${escaped}\\b`, 'i');
        }

        if (length <= 4) {
            const loose = escaped.split('').join('.{0,2}');
            return new RegExp(loose, 'i');
        }

        const fuzzy = escaped.split('').join('.*');
        return new RegExp(fuzzy, 'i');
    };

    return terms.every((term) => {
        const regex = fuzzify(term);
        return regex.test(name);
    });
};

    const generateUUID = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
            /[xy]/g,
            function (c) {
                var r = (Math.random() * 16) | 0,
                    v = c == 'x' ? r : (r & 0x3) | 0x8;
                return v.toString(16);
            },
        );
    };

    useEffect(() => {
        if (isOpen) {
            setProducts([]);
            setCurrentPage(1);
            setHasMore(true);
            setAllProductsLoaded(false);
            setUseLightStock(true);
            setSelectedPackagings({});
            setSearch('');
            setRefSearch('');
            setEanSearch('');
            // Load liked products
            if (customerId) {
                getCustomerLikedProducts(customerId).then((res) => {
                    if (res) setLikedProductIds(res.likedProducts || []);
                });
            }
            // Load already ordered products first then the others
            loadAlreadyOrderedProducts().then((orderedProducts) => {
                loadProducts(1, orderedProducts);
            });
        }
    }, [isOpen]);

    useEffect(() => {

        const container = scrollContainerRef.current;
        if (!container) return;

        const handleScroll = () => {
            if (isLoading || !hasMore) return;
            
            const { scrollTop, scrollHeight, clientHeight } = container;
            if (scrollTop + clientHeight >= scrollHeight - 200) {
                const nextPage = currentPage + 1;
                setCurrentPage(nextPage);
                loadProducts(nextPage);
            }
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [currentPage, isLoading, hasMore]);

    // Server-side search: when the user types (name, ref or EAN), query the WHOLE
    // catalog so products beyond the initially loaded set (large catalogs) are
    // still found. Matches are merged into the local list (deduplicated) — purely
    // additive. Only the matches travel over the network, keeping memory low.
    useEffect(() => {
        const terms = [search, refSearch, eanSearch]
            .map((t) => (t || '').trim())
            .filter((t) => t.length >= 2);
        if (terms.length === 0) return;
        const term = terms.join(' ');
        let cancelled = false;
        const handler = setTimeout(async () => {
            const res = await getProducts(1, 500, customerId, term);
            if (cancelled || !res?.data) return;
            mergeProducts(res.data);
        }, 300);
        return () => {
            cancelled = true;
            clearTimeout(handler);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, refSearch, eanSearch, customerId]);

    const handleCustomerProductChange = (
        index: number,
        key: string,
        value: any,
    ) => {
        const updated = customProducts.map((item, i) =>
            i === index
                ? {
                      ...item,
                      [key]: value,
                  }
                : item,
        );
        setCustomProducts(updated);

        // Garder une ligne personnalisée déjà sélectionnée (quantité saisie) en
        // phase avec les champs édités (nom, unité, TVA, prix) afin qu'ils soient
        // bien transmis à la commande puis aux documents officiels
        // (facture, bon de livraison...).
        const changedItem = updated[index];
        if (changedItem?._id) {
            setSelectedProducts((prev) =>
                prev.map((p) =>
                    p.productId === changedItem._id
                        ? {
                              ...p,
                              name: changedItem.name,
                              unity: changedItem.unity,
                              vat: parseFloat(changedItem.vat) || 0,
                              price: parseFloat(changedItem.price) || 0,
                          }
                        : p,
                ),
            );
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay className="bg-[#000] !opacity-30" />
            <ModalContent className="relative !z-50 mx-2 sm:mx-auto md:top-[7vh]">
                <ModalBody>
                    <Card extra="mt-5 lg:mt-0 px-3 sm:px-[10px] lg:px-[30px] pt-[25px] sm:pt-[35px] pb-[30px] sm:pb-[40px] max-w-full sm:max-w-[95%] lg:max-w-[70%] w-full mx-auto flex flex-col" style={{ maxHeight: '85vh' }}>
                        <div className="absolute top-4 right-4">
                            <button
                                className="text-red-500 hover:text-red-600 hover:cursor-pointer"
                                onClick={onClose}
                            >
                                <MdClose size={20} />
                            </button>
                        </div>
                        <h1 className="text-base sm:text-lg font-bold">
                            Ajouter des produits
                        </h1>
                        <p className="mt-2 text-xs sm:text-sm text-gray-600">
                            Cliquez sur les produits que vous souhaitez ajouter
                            à la commande
                        </p>
                        <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-12">
                            <div className="col-span-1 sm:col-span-1 lg:col-span-3">
                                <div className="mt-3 flex h-[35px] flex-grow items-center rounded-xl bg-lightPrimary text-sm text-gray-600">
                                    <SearchIcon />
                                    <input
                                        type="text"
                                        placeholder="Rechercher par référence"
                                        value={refSearch}
                                        onChange={(e) =>
                                            setRefSearch(e.target.value)
                                        }
                                        className="block w-full rounded-full bg-lightPrimary text-sm text-navy-700 outline-none"
                                    />
                                </div>
                            </div>
                            <div className="col-span-1 sm:col-span-1 lg:col-span-3">
                                <div className="mt-3 flex h-[35px] flex-grow items-center rounded-xl bg-lightPrimary text-sm text-gray-600">
                                    <SearchIcon />
                                    <input
                                        type="text"
                                        placeholder="Rechercher par code EAN"
                                        value={eanSearch}
                                        onChange={(e) =>
                                            setEanSearch(e.target.value)
                                        }
                                        className="block w-full rounded-full bg-lightPrimary text-sm text-navy-700 outline-none"
                                    />
                                </div>
                            </div>
                            <div className="col-span-1 sm:col-span-1 lg:col-span-6">
                                <div className="mt-3 flex h-[35px] flex-grow items-center rounded-xl bg-lightPrimary text-sm text-gray-600">
                                    <SearchIcon />
                                    <input
                                        type="text"
                                        placeholder="Rechercher par nom produit"
                                        value={search}
                                        onChange={(e) =>
                                            setSearch(e.target.value)
                                        }
                                        className="block w-full rounded-full bg-lightPrimary text-sm text-navy-700 outline-none"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-wrap mt-3 ml-2 sm:ml-4 gap-2 sm:gap-4">
                            <p
                                className={`text-xs ${
                                    tab === 'all'
                                        ? 'text-toola-500 underline'
                                        : 'text-toola-500/80'
                                } hover:underline hover:cursor-pointer`}
                                onClick={() => setTab('all')}
                            >
                                Tout afficher
                            </p>
                            <p
                                className={`text-xs ${
                                    tab === 'recent-order'
                                        ? 'text-toola-500 underline'
                                        : 'text-toola-500/80'
                                } hover:underline hover:cursor-pointer`}
                                onClick={() => setTab('recent-order')}
                            >
                                Déjà commandé
                            </p>
                            <p
                                className={`text-xs ${
                                    tab === 'custom-price'
                                        ? 'text-toola-500 underline'
                                        : 'text-toola-500/80'
                                } hover:underline hover:cursor-pointer`}
                                onClick={() => setTab('custom-price')}
                            >
                                Avec prix personnalisés
                            </p>
                            {likedProductIds.length > 0 && (
                                <p
                                    className={`flex items-center gap-1 text-xs ${
                                        tab === 'liked'
                                            ? 'text-pink-500 underline'
                                            : 'text-pink-400'
                                    } hover:underline hover:cursor-pointer`}
                                    onClick={() => setTab('liked')}
                                >
                                    <FaHeart size={10} />
                                    Favoris
                                </p>
                            )}
                        </div>
                        <div ref={scrollContainerRef} className="mx-1 mt-3 grid grid-cols-1 gap-3 overflow-y-auto overflow-x-auto flex-1">
                            <table className="w-full text-xs sm:text-sm text-left rtl:text-right text-gray-500">
                                <thead className="text-[10px] sm:text-xs text-gray-700 bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-1 sm:px-2 py-2 sm:py-3 min-w-[120px]">
                                            Produit
                                        </th>
                                        <th scope="col" className="px-1 sm:px-2 py-2 sm:py-3 hidden sm:table-cell">
                                            Conditionnement
                                        </th>
                                        <th scope="col" className="px-1 sm:px-2 py-2 sm:py-3">
                                            Prix HT
                                        </th>
                                        <th scope="col" className="px-1 sm:px-2 py-2 sm:py-3 hidden md:table-cell">
                                            Stock
                                        </th>
                                        <th scope="col" className="px-1 sm:px-2 py-2 sm:py-3">
                                            Qté
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {customProducts.map((item, index) => (
                                        <tr
                                            className="bg-white border-b text-gray-800 border-gray-200 hover:bg-gray-100"
                                            key={`custom-${item._id}`}
                                        >
                                            {/* Product : name + vat + unity (mobile) */}
                                            <td className="px-1 sm:px-2 py-2 align-top">
                                                <div className="flex flex-col gap-2">
                                                    <input
                                                        type="text"
                                                        className="px-2 w-full sm:w-[200px] h-[30px] text-xs sm:text-sm border border-gray-200 rounded-xl"
                                                        placeholder="Nom du produit/service"
                                                        value={item.name}
                                                        onChange={(e) =>
                                                            handleCustomerProductChange(
                                                                index,
                                                                'name',
                                                                e.target.value,
                                                            )
                                                        }
                                                    />
                                                    <div className="flex gap-2">
                                                        <input
                                                            type="number"
                                                            placeholder="TVA (%)"
                                                            className="px-2 text-[10px] sm:text-xs w-[60px] sm:w-[70px] h-[30px] border border-gray-200 rounded-xl"
                                                            value={item.vat}
                                                            onChange={(e) =>
                                                                handleCustomerProductChange(
                                                                    index,
                                                                    'vat',
                                                                    e.target.value,
                                                                )
                                                            }
                                                        />
                                                        {/* Unity visible only on mobile (the Packaging column is hidden) */}
                                                        <input
                                                            type="text"
                                                            placeholder="Unité"
                                                            className="px-2 text-[10px] sm:text-xs w-[80px] h-[30px] border border-gray-200 rounded-xl sm:hidden"
                                                            value={item.unity || ''}
                                                            onChange={(e) =>
                                                                handleCustomerProductChange(
                                                                    index,
                                                                    'unity',
                                                                    e.target.value,
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            </td>
                                            {/* Packaging / unity (desktop) */}
                                            <td className="px-1 sm:px-2 py-2 sm:py-3 hidden sm:table-cell align-top">
                                                <input
                                                    type="text"
                                                    placeholder="Unité (kg, pièce...)"
                                                    className="px-2 w-full max-w-[140px] h-[30px] text-[10px] sm:text-xs border border-gray-200 rounded-xl"
                                                    value={item.unity || ''}
                                                    onChange={(e) =>
                                                        handleCustomerProductChange(
                                                            index,
                                                            'unity',
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            </td>
                                            {/* Price HT */}
                                            <td className="px-1 sm:px-2 py-2 sm:py-3 align-top">
                                                <div className="flex items-center gap-1">
                                                    <input
                                                        type="number"
                                                        className="px-2 w-[60px] sm:w-[70px] h-[30px] text-xs sm:text-sm border border-gray-200 rounded-xl"
                                                        placeholder="0"
                                                        value={item.price}
                                                        onChange={(e) =>
                                                            handleCustomerProductChange(
                                                                index,
                                                                'price',
                                                                e.target.value,
                                                            )
                                                        }
                                                    />
                                                    <span className="text-xs sm:text-sm">€</span>
                                                </div>
                                            </td>
                                            {/* Stock (not applicable for a custom line) */}
                                            <td className="px-1 sm:px-2 py-2 sm:py-3 hidden md:table-cell align-top">
                                                <span className="text-xs text-gray-400">—</span>
                                            </td>
                                            {/* Quantity */}
                                            <td className="px-1 sm:px-2 py-2 sm:py-3 align-top">
                                                <input
                                                    type="number"
                                                    step="any"
                                                    className="px-2 w-[60px] sm:w-[80px] h-[30px] text-xs sm:text-sm border border-gray-200 rounded-xl"
                                                    placeholder="0"
                                                    value={
                                                        selectedProducts.find(
                                                            (i) =>
                                                                i.productId ===
                                                                item._id,
                                                        )?.quantity ?? ''
                                                    }
                                                    onChange={(e) =>
                                                        handleItemClicked(
                                                            item,
                                                            parseFloat(
                                                                e.target.value,
                                                            ),
                                                        )
                                                    }
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                    {products &&
                                        products
                                            .filter((i: any) =>
                                                searchProduct(
                                                    i,
                                                    search,
                                                    refSearch,
                                                    eanSearch,
                                                ),
                                            )
                                            .filter((i: any) =>
                                                tab === 'recent-order'
                                                    ? i.isRecent
                                                    : tab === 'custom-price'
                                                      ? i.customPrice
                                                      : tab === 'liked'
                                                        ? likedProductIds.includes(i._id)
                                                        : i,
                                            )
                                            .filter(
                                                (i: any) =>
                                                    !existingProducts.includes(
                                                        i._id,
                                                    ),
                                            ).length === 0 && (
                                            <tr
                                                className="bg-white border-b text-gray-800 border-gray-200 hover:bg-gray-100 cursor-pointer"
                                                key="no-product"
                                            >
                                                <td className="px-1 sm:px-2 py-2 text-xs sm:text-sm">
                                                    Aucun produit trouvé
                                                </td>
                                                <td className="px-1 sm:px-2 py-3 hidden sm:table-cell"></td>
                                                <td className="px-1 sm:px-2 py-3"></td>
                                                <td className="px-1 sm:px-2 py-3 hidden md:table-cell"></td>
                                                <td className="px-1 sm:px-2 py-3"></td>
                                            </tr>
                                        )}
                                    {products &&
                                        products
                                            .filter((i: any) => !i._id?.toString().startsWith('custom-'))
                                            .filter((i: any) =>
                                                searchProduct(
                                                    i,
                                                    search,
                                                    refSearch,
                                                    eanSearch,
                                                ),
                                            )
                                            .filter((i: any) =>
                                                tab === 'recent-order'
                                                    ? i.isRecent
                                                    : tab === 'custom-price'
                                                      ? i.customPrice
                                                      : tab === 'liked'
                                                        ? likedProductIds.includes(i._id)
                                                        : i,
                                            )
                                            
                                            .filter(
                                                (i: any) =>
                                                    !existingProducts.includes(
                                                        i._id,
                                                    ),
                                            )
                                            .map((item: any, index: number) => (
                                                <tr
                                                    className="bg-white border-b text-gray-800 border-gray-200 hover:bg-gray-100 cursor-pointer"
                                                    onClick={() => {}}
                                                    key={`product-${item._id}`}
                                                >
                                                    <td className="px-1 sm:px-2 py-2">
                                                        {/* Created 10 minutes ago ? Live ping */}

                                                        <div className="flex flex-col sm:flex-row my-auto gap-1 sm:gap-2">
                                                            <p>
                                                                <p className="text-[10px] sm:text-sm">
                                                                    #{item.ref}
                                                                </p>
                                                            </p>
                                                            <span className="hidden sm:inline">{' - '}</span>
                                                            <p className="text-xs sm:text-sm flex my-auto text-gray-700 break-words">
                                                                {item.name}
                                                            </p>
                                                            {item?.isRecent && (
                                                                <p className="flex gap-1 my-auto text-sm text-pink-500" title="Déjà commandé">
                                                                    <MdOutlineFavorite />
                                                                </p>
                                                            )}
                                                            {likedProductIds.includes(item?._id) && (
                                                                <p className="flex gap-1 my-auto text-sm text-pink-500" title="Produit favori">
                                                                    <FaHeart size={13} />
                                                                </p>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-1 sm:px-2 py-2 sm:py-3 hidden sm:table-cell">
                                                        {item.packagings && item.packagings.length > 0 ? (
                                                            <select
                                                                className="px-1 sm:px-2 py-1 text-[10px] sm:text-xs border border-gray-200 rounded-xl bg-white max-w-[120px]"
                                                                value={
                                                                    selectedPackagings[item._id]
                                                                        ? item.packagings.findIndex((p: any) => p === selectedPackagings[item._id]).toString()
                                                                        : ''
                                                                }
                                                                onChange={(e) => {
                                                                    if (e.target.value === '') {
                                                                        setSelectedPackagings((prev) => ({
                                                                            ...prev,
                                                                            [item._id]: null
                                                                        }));
                                                                    } else {
                                                                        const packaging = item.packagings[parseInt(e.target.value)];
                                                                        setSelectedPackagings((prev) => ({
                                                                            ...prev,
                                                                            [item._id]: packaging || null
                                                                        }));
                                                                    }
                                                                }}
                                                            >
                                                                <option value="">
                                                                    {item.unity} - {item.price}€
                                                                </option>
                                                                {item.packagings.map((pkg: any, idx: number) => (
                                                                    <option key={idx} value={idx.toString()}>
                                                                        {pkg.unity} ({pkg.weight} {item.unity}) - {pkg.price}€
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        ) : (
                                                            <span className="text-xs text-gray-500">
                                                                {item.unity}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-1 sm:px-2 py-2 sm:py-3">
                                                        <span className="text-xs sm:text-sm">{selectedPackagings[item._id]?.price || item?.price} €</span>
                                                    </td>
                                                    <td className="px-1 sm:px-2 py-2 sm:py-3 hidden md:table-cell">
                                                        <span className={`text-xs sm:text-sm ${item?.quantity > 0 ? 'text-green-600' : 'text-red-500'}`}>
                                                            {(item?.quantity || 0).toFixed(2)} {item?.unity || ''}
                                                        </span>
                                                        {item?.draftQuantity > 0 && (
                                                            <span className="block text-[10px] sm:text-xs text-orange-500" title="Quantité prévue dans des commandes en brouillon">
                                                                -{item.draftQuantity.toFixed(2)} en brouillon
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-1 sm:px-2 py-2 sm:py-3">
                                                        <input
                                                            type="number"
                                                            step="any"
                                                            className="px-2 w-[60px] sm:w-[80px] h-[30px] text-xs sm:text-sm border border-gray-200 rounded-xl"
                                                            placeholder="0"
                                                            value={
                                                                selectedProducts.find(
                                                                    (i) =>
                                                                        i.productId ===
                                                                        item._id,
                                                                )?.quantity ?? ''
                                                            }
                                                            onChange={(e) =>
                                                                handleItemClicked(
                                                                    item,
                                                                    parseFloat(
                                                                        e.target
                                                                            .value,
                                                                    ),
                                                                )
                                                            }
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                </tbody>
                            </table>
                            {isLoading && (
                                <div className="py-4 text-center text-sm text-gray-500">
                                    Chargement...
                                </div>
                            )}
                        </div>
                        <div className="mt-3 flex gap-1 px-1">
                            <MdAddBox
                                size={15}
                                className="flex my-auto text-toola-500"
                            />
                            <p
                                className="flex text-xs sm:text-sm my-auto text-toola-500 hover:cursor-pointer hover:text-toola-600"
                                onClick={() => {
                                    setCustomProducts((prev) => [
                                        ...prev,
                                        {
                                            name: '',
                                            price: 0,
                                            unity: 'unité',
                                            _id: `custom-${generateUUID()}`,
                                        },
                                    ]);
                                }}
                            >
                                Ajouter une ligne personnalisée
                            </p>
                        </div>
                        <div className="mt-5 flex gap-2 px-1">
                            <button
                                className="linear flex-1 sm:flex-none flex items-center justify-center rounded-xl bg-[#0260CB] px-4 sm:px-6 py-3 sm:py-2 text-sm sm:text-xs font-medium text-white hover:bg-[#01499b] active:bg-[#0260CB]"
                                onClick={() => {
                                    addProducts(selectedProducts);
                                    setSelectedProducts([]);
                                    setCustomProducts([]);
                                }}
                            >
                                Ajouter
                            </button>
                        </div>
                    </Card>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default AddProductsModal;
